import _ from 'lodash'
import localforage from 'localforage'
import { from } from 'rxjs'
import { map } from 'rxjs/operators'

import { DataSynchronisationStatus } from '@skinsoft/types'

interface DataUpdateCacherStats {
    date: number,
    status: DataSynchronisationStatus
}

export const offlineDataUpdateCache = localforage.createInstance({
    driver: localforage.INDEXEDDB,
    name: 'reporter-pending-update-data-cache',
    storeName: 'runtime_cache',
    version: 2.0,
});
const LAST_PUSH_DATE_CACHE_KEY = 'last-push-date';
export class OfflineDataUpdateCacher<T extends any> {

    // methods to manage push stats
    public static async setLastPushStats(date: number, status: DataSynchronisationStatus) {
        await offlineDataUpdateCache.setItem(LAST_PUSH_DATE_CACHE_KEY, { date, status })
    }

    public static async getLastPushStats(): Promise<DataUpdateCacherStats> {
        return await offlineDataUpdateCache.getItem(LAST_PUSH_DATE_CACHE_KEY)
    }

    public static async isPushError(): Promise<boolean> {
        const stats = await this.getLastPushStats();
        if (stats) {
            return stats.status === DataSynchronisationStatus.ERROR ||
                stats.status === DataSynchronisationStatus.ABORTED
        }
        return false;
    }

    private cacheKeyPrefix: string = "";

    constructor(cacheKey: string) {
        this.cacheKeyPrefix = cacheKey;
    }

    public storeBeforePushing(id: string, body: Partial<T> & {id: string}): Promise<Partial<T> | null> {
        if (id) {
            Object.defineProperty(body, "fromUpdateCache", {
                enumerable: true,
                value: true,
            })
            return offlineDataUpdateCache.setItem(this.getCacheKey(id), body)
        }
        return Promise.resolve(null)
    }

    public deleteBeforePushing(id: string) {
        if (id) {
            offlineDataUpdateCache.removeItem(this.getCacheKey(id))
        }
    }

    public getCacheKey(id: string): string {
        return `${this.cacheKeyPrefix}-${id}`;
    }

    public getSanitizedId(id: string) {
        return id.substring(id.lastIndexOf('-') + 1);
    }

    public getCachedValue(id: string): Promise<T> {
        const cacheKey = this.getCacheKey(id);
        return offlineDataUpdateCache.getItem(cacheKey)
    }

    public removeCachedValue(id: string) {
        const cacheKey = this.getCacheKey(id);
        return offlineDataUpdateCache.removeItem(cacheKey)
    }

    public async renameCacheKey(id: string, newId: string) {
        let value = await this.getCachedValue(id) as unknown as Partial<T> & { id: string }
        value.id = newId
        this.storeBeforePushing(newId, value)
        await this.removeCachedValue(id)
    }

    public async getCachedObjectsKeys(countLocallyCreated: boolean = true): Promise<string[]> {
        const theRealThis = this
        const keys = await offlineDataUpdateCache.keys()
        return keys.filter(
            key => {
                const matchPrefix = key.includes(`${theRealThis.cacheKeyPrefix}-`)
                const matchNew = !key.includes('_')

                return countLocallyCreated ? matchPrefix : matchNew && matchPrefix
            }
        )
    }
    /**
     * Returns all objects that were locally created in offline mode, 
     * and not pushed to the db yet
     */
    public async getLocallyCreatedCachedObjects(): Promise<T[]> {
        return this.getObjectsWithPredicate(id => id.includes('_'))
    }

    /**
     * Returns all objects that were locally modified in offline mode, 
     * and not pushed to the db yet
     */
    public async getLocallyUpdatedCachedObjects() {
        return await this.getObjectsWithPredicate(id => !id.includes('_'))
    }

    public async getAllObjects(): Promise<T[]> {
        let result: T[] = []

        const keys = await offlineDataUpdateCache.keys()
        for(const key of keys) {
            const item = await offlineDataUpdateCache.getItem(key)
            if(item) {
                result.push(item as T)
            }
        }

        return result
    }

    /**
     * get all cached entity that are really entities (exludes stats and undefined)
     */
    public async getAllRealObjects(includeCashedCreations: boolean = true): Promise<T[]> {
        let result: T[] = []

        const keys = await this.getCachedObjectsKeys(includeCashedCreations)
        for(const key of keys) {
            const item = await offlineDataUpdateCache.getItem(key)
            if(item) {
                result.push(item as T)
            }
        }

        return result
    }

    public async getNbCachedObjects(countLocallyCreated: boolean = true) {
        return (await this.getCachedObjectsKeys(countLocallyCreated)).length;
    }

    public async getNbCachedCreations() {
        return (await this.getCachedObjectsKeys(true)).filter(key => {
            return OfflineDataUpdateCacher.isTemporaryId(
                this.getSanitizedId(key)
            )
        }).length
    }

    public async fetchOneFromId(idToMatch: string) {
        const matching = await this.getObjectsWithPredicate(id => {
            return id.includes(idToMatch)
        })
        return matching && matching[0] ? matching[0] : null;
    }

    /**
     * Returns the objects in cache matching a predicate
     * @param predicate 
     */
    private async getObjectsWithPredicate(predicate: (id: string) => boolean) {
        return from(this.getCachedObjectsKeys()).pipe(
            map(keys => {
                return keys.filter(predicate)
            }),
            map(
                filteredKeys => {
                    return Promise.all(filteredKeys.map(
                        async key => {
                            const entity = await offlineDataUpdateCache.getItem<T>(key)
                            return entity;
                        }
                    ));
                }
            )
        ).toPromise();
    }

    public static async clear() {
        return offlineDataUpdateCache.clear()
    }

    public static isTemporaryId(id: string): boolean {
        return id?.length > 0 && 
            (typeof id === 'string' || (id as any) instanceof String) && 
            id.startsWith('_')
    }
}